'use strict';

var map;
var marker;
var locationData = {};
var googleMapsLoader = require('./googleMapsLoader');

/**
 * Parses Google Maps address components to a structured address object.
 *
 * @param {Array} components - Array of address components from Google Maps API.
 * @returns {Object} Parsed address containing address1, address2, city, state, pincode, country, and formatted_address.
 */
function parseAddressComponents(components) {
    var address1 = '';
    var address2 = '';
    var address3 = '';
    var city = '';
    var state = '';
    var pincode = '';
    var country = '';

    components.forEach(function (component) {
        var types = component.types;

        if (types.includes('premise') && !address1) {
            address1 = component.long_name;
        } else if (types.includes('route') && !address2) {
            address2 = component.short_name;
        } else if (types.includes('sublocality_level_1') && !address3) {
            address3 = component.short_name;
        } else if (types.includes('locality') && !city) {
            city = component.short_name;
        } else if (types.includes('administrative_area_level_1') && !state) {
            state = component.long_name;
        } else if (types.includes('postal_code') && !pincode) {
            pincode = component.short_name;
        } else if (types.includes('country') && !country) {
            country = component.long_name;
        }
    });

    var combinedAddress2 = address3 ? address2 + ', ' + address3 : address2;
    var parts = [address1, combinedAddress2, city, state, country];
    var formattedaddress = '';

    parts.forEach(function (item) {
        if (typeof item === 'string') {
            var cleanItem = item.replace(/,/g, '').trim();
            if (cleanItem) {
                formattedaddress = formattedaddress
                    ? formattedaddress + ', ' + cleanItem
                    : cleanItem;
            }
        }
    });

    return {
        address1: address1,
        address2: combinedAddress2,
        city: city,
        state: state,
        pincode: pincode,
        country: country,
        formattedaddress: formattedaddress
    };
}

/**
 * Create or update Google Map marker
 * @param {Object} position - Google Maps LatLng position
 */
function updateMapMarker(position) {
    var mapMarkerURL = $('#registryMapMarkerUrl').val();

    if (!marker) {
        // eslint-disable-next-line no-undef
        marker = new google.maps.Marker({
            position: position,
            map: map,
            clickable: false,
            icon: mapMarkerURL ? {
                url: mapMarkerURL,
                // eslint-disable-next-line no-undef
                scaledSize: new google.maps.Size(40, 40),
                // eslint-disable-next-line no-undef
                origin: new google.maps.Point(0, 0),
                // eslint-disable-next-line no-undef
                anchor: new google.maps.Point(20, 40)
            } : null
        });
    } else {
        marker.setPosition(position);
    }
}

/**
 * Updates address display based on the current marker position using reverse geocoding.
 *
 * @param {google.maps.Geocoder} geocoder - Google Maps Geocoder instance.
 */
function updateAddressFromMarkerPosition(geocoder) {
    if (!map) return;

    var position = map.getCenter();
    if (!position) return;

    geocoder.geocode({ location: position }, function (results, status) {
        if (status === 'OK' && results[0]) {
            var addressComponents = results[0].address_components;
            var countryComponent = addressComponents.find(function (component) {
                return component.types.includes('country');
            });

            if (countryComponent && countryComponent.short_name === 'IN') {
                locationData = parseAddressComponents(addressComponents);
                $('#registryLocationFromMap').text(locationData.formattedaddress);
            }
        } else {
            $('#registryLocationFromMap').text('Address not found');
            locationData = {};
        }
    });
}

/**
 * Initializes the Google Map for registry location selection.
 * Creates the map once, enables Places Autocomplete, and manages marker placement.
 * Updates address data on place selection, map click, or geolocation.
 * @returns {void}
 */
function initializeRegistryMap() {
    if (map) return map;

    var defaultLocation = { lat: 12.9716, lng: 77.5946 };
    // eslint-disable-next-line no-undef
    var geocoder = new google.maps.Geocoder();

    // eslint-disable-next-line no-undef
    map = new google.maps.Map(document.getElementById('registryMap'), {
        center: defaultLocation,
        zoom: 15,
        streetViewControl: false,
        zoomControl: true,
        rotateControl: false,
        mapTypeControl: true,
        fullscreenControl: true,
        scaleControl: false
    });

    updateMapMarker(defaultLocation);

    map.addListener('center_changed', function () {
        if (marker) {
            marker.setPosition(map.getCenter());
        }
    });

    map.addListener('dragend', function () {
        updateAddressFromMarkerPosition(geocoder);
    });

    var $input = $('#registryMapInput');
    // eslint-disable-next-line no-undef
    var autocomplete = new google.maps.places.Autocomplete($input[0], {
        componentRestrictions: { country: ['in'] }
    });

    autocomplete.bindTo('bounds', map);

    autocomplete.addListener('place_changed', function () {
        var place = autocomplete.getPlace();

        if (!place.geometry || !place.geometry.location) {
            return;
        }

        var locationLatLng = place.geometry.location;

        if (place.geometry.viewport) {
            map.fitBounds(place.geometry.viewport);
        } else {
            map.setCenter(locationLatLng);
            map.setZoom(15);
        }

        locationData = parseAddressComponents(place.address_components);
        $('#registryLocationFromMap').text(locationData.formattedaddress);

        // Auto-update values logic if needed immediately, but we have a confirm button
    });

    // Try to get current location for initial map center
    if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(function (position) {
            var pos = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
            };
            map.setCenter(pos);
            updateMapMarker(pos);
            updateAddressFromMarkerPosition(geocoder);
        });
    }

    return map;
}

/**
 * Lazily loads the Google Maps SDK before initializing the registry modal map.
 *
 * @returns {Promise<Object>} Resolves with the map instance.
 */
function initRegistryMap() {
    return googleMapsLoader.load().then(function () {
        return initializeRegistryMap();
    });
}

/**
 * Fills registry address fields using parsed location data.
 * Populates address line 1, line 2, city, and pincode inputs.
 * Works within the active registry step or falls back to body.
 * @returns {void}
 */
function fillRegistryAddress() {
    if (!locationData) return;

    var $container = $('.registry-step:not(.d-none)');
    if (!$container.length) $container = $('body');

    $container.find('.registry-address-one').val(locationData.address1 || '');
    $container.find('.registry-address-two').val(locationData.address2 || '');
    $container.find('.registry-address-city').val(locationData.city || '');
    $container.find('.registry-address-pincode').val(locationData.pincode || '');

    $container.find('.registry-address-one, .registry-address-two, .registry-address-city, .registry-address-pincode').trigger('change');
}

/**
 * Fetches the user’s current geolocation and resolves it to an address.
 * Uses Google Geocoder to parse address components.
 * Updates registry address fields on success.
 * @returns {void}
 */
function getCurrentLocationForRegistry() {
    googleMapsLoader.load().then(function () {
        if (!navigator.geolocation) {
            $.spinner().stop();
            return;
        }

        navigator.geolocation.getCurrentPosition(
            function (position) {
                var latlng = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                };

                // eslint-disable-next-line no-undef
                var geocoder = new google.maps.Geocoder();

                geocoder.geocode({ location: latlng }, function (results, status) {
                    if (status === 'OK' && results[0]) {
                        locationData = parseAddressComponents(results[0].address_components);
                        fillRegistryAddress();
                    }
                    $.spinner().stop();
                });
            },
            function () {
                $.spinner().stop();
            }
        );
    }).catch(function () {
        $.spinner().stop();
    });
}

$('body').on('click', '.btn-current-location', function (e) {
    e.preventDefault();
    $.spinner().start();
    if (navigator.permissions) {
        navigator.permissions.query({ name: 'geolocation' }).then(function (result) {
            if (result.state === 'denied') {
                $.spinner().stop();

                // eslint-disable-next-line no-alert
                alert('Location access is denied. Please enable it in your browser settings.');
            } else {
                getCurrentLocationForRegistry();
            }
        });
    } else {
        getCurrentLocationForRegistry();
    }
});

$(document).ready(function () {
    // Move modal to body to ensure it is not clipped by parent containers
    var $modal = $('#registryLocationModal');
    if ($modal.length && !$modal.parent().is('body')) {
        $modal.appendTo('body');
    }

    $('body').on('click', '.locate-btn-on-map', function () {
        $('#registryLocationModal').modal('show');
        setTimeout(function () {
            initRegistryMap().then(function () {
                // eslint-disable-next-line no-undef
                if (map && typeof google !== 'undefined') {
                    // eslint-disable-next-line no-undef
                    google.maps.event.trigger(map, 'resize');
                    if (marker) {
                        map.setCenter(marker.getPosition());
                    }
                }
            });
        }, 500);
    });

    $('body').on('click', '#btnRegistryLocationConfirm', function () {
        fillRegistryAddress();
        $('#registryLocationModal').modal('hide');
    });
});
